import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import format from 'date-fns/format';
import React from 'react';
import { IntegrationSystemDisplayNameType } from 'shared/providers/IntegrationsProvider';
import {
  FinancialTransactionType,
  PaymentReportFinancialTransactionFragment,
  StripePaymentIntentStatus,
} from 'shared/types/graphql';
import { capitalize, getBalanceTerminology, getInvoiceIdentifierCopy } from 'shared/utils';
import { getWritebackStatus } from '.';
import TooltipText from '../components/TooltipText';
import { DATE_FORMAT, writebackStatusDisplayValue } from '../constants';
import { DisplayType, IColumnSelection } from '../types';
import { formatMoney } from './formatMoney';
import { dataPlaceholder, getStatusComponent } from './formatting';
import WritebackStatus from '../components/WritebackStatus';
import { WritebackStatusDescription } from '../records/writeback.records';
import { Link } from '@televet/kibble-ui/build/components/Link';
import PaymentMoreButton from '../components/PaymentMoreButton';
import { StatusDescription } from '../records/status.records';
import { centsToDollars } from 'shared/components/SidePanel/components/SearchPanel/components/Benefits/utils/currency';
import { PaymentReportStripePaymentIntent } from '../records/transactions.records';

interface BaseTransactionsData {
  rawData: PaymentReportFinancialTransactionFragment[];
  displayType: DisplayType;
  isPaymentConfigInvalid: boolean;
  primaryIntegrationName: IntegrationSystemDisplayNameType | null;
  hasAdminRoleAtCurrentClinic: boolean;
  hasInvoiceDiscovery: boolean;
  hasBitwerxPaymentWritebacks: boolean;
  isClientIdSupported: boolean;
}

interface InteractiveTransactionsData extends BaseTransactionsData {
  displayType: 'Interactive';
  callbacks: {
    onDeveloperLinkClick: (data: PaymentReportFinancialTransactionFragment) => void;
    onPetParentClick: (id: string) => void;
    onConversationClick: (channelId: string) => void;
    setRefundPaymentIntent: React.Dispatch<React.SetStateAction<PaymentReportStripePaymentIntent | undefined>>;
  };
  columns?: never;
}

interface DownloadTransactionsData extends BaseTransactionsData {
  displayType: 'Download';
  callbacks?: never;
  columns: IColumnSelection[];
}

type BuildTransactionsDataInput = InteractiveTransactionsData | DownloadTransactionsData;

interface BaseTransactionTableRow {
  cardBrand: string;
  cardType: string;
  date: string;
  disbursed: string;
  last4: string;
  terminal: string;
}

interface ControlledFields {
  clinicPetParentPimsId?: string; // Gated by isClientIdSupported
  invoices?: string; // Optional since it depends on a FF
  writebackStatus?: JSX.Element | string; // Optional since it depends on a FF
}

interface InteractiveTransactionTableRow extends BaseTransactionTableRow, ControlledFields {
  actions: JSX.Element | string;
  amount: string;
  clientTransactionFee: string;
  clinicPetParentName: JSX.Element | string;
  clinicPetParentNameFiltering: string;
  conversation: JSX.Element | string;
  developerMode: JSX.Element;
  netProceeds: string;
  paymentMethod: JSX.Element | string;
  paymentStatus: JSX.Element | string;
  sentBy: JSX.Element | string;
  totalInvoiced: string;
  transactionFees: string;
  transactionType: JSX.Element;
}

interface DownloadTransactionTableRow extends BaseTransactionTableRow, ControlledFields {
  amount: number;
  clientTransactionFee: number | string;
  clinicPetParentName: string;
  clinicPetParentNameFiltering?: string;
  netProceeds: number;
  paymentMethod: string;
  paymentStatus: string;
  sentBy: string;
  totalInvoiced: number | string;
  transactionFees: number | string;
  transactionType: string;
  writebackStatus?: string;
}

export type TransactionTableRow = InteractiveTransactionTableRow | DownloadTransactionTableRow;

const getTransactionTypeDescription = ({
  type,
  amount,
  refundedAmount,
  adjustmentDescription,
}: {
  type: FinancialTransactionType;
  amount?: number;
  refundedAmount: number;
  adjustmentDescription?: string | null;
}): string => {
  switch (type) {
    case FinancialTransactionType.Adjustment:
      return `Adjustment: ${adjustmentDescription || ''}`;
    case FinancialTransactionType.DirectBookingDeposit:
      return 'Direct Booking Deposit';
    case FinancialTransactionType.Dispute:
      return 'Dispute';
    case FinancialTransactionType.Refund:
      return Math.abs(refundedAmount) < Math.abs(amount || 0) ? 'Partial Refund' : 'Refund';
    case FinancialTransactionType.Sale:
      return 'Payment';
    case FinancialTransactionType.Subscription:
      return 'Subscription';
    default:
      return '';
  }
};

export const buildTransactionsData = ({
  rawData,
  displayType,
  isPaymentConfigInvalid,
  primaryIntegrationName,
  hasAdminRoleAtCurrentClinic,
  callbacks,
  hasBitwerxPaymentWritebacks,
  hasInvoiceDiscovery,
  isClientIdSupported,
  columns,
}: BuildTransactionsDataInput): TransactionTableRow[] => {
  if (!rawData.length) {
    return [];
  }
  const emptyPlaceholder = dataPlaceholder[displayType];
  const isInteractiveDisplay = displayType === 'Interactive';

  const tableData: TransactionTableRow[] = [];

  for (const data of rawData) {
    const {
      cardBrand,
      cardType,
      timestamp,
      arrivalDate,
      last4,
      terminalName,
      clientFee,
      clientName,
      clientPetParentId,
      channelId,
      transactionId,
      netProceeds,
      paymentMedium,
      paymentStatus,
      sentBy,
      totalInvoiced,
      transactionFees,
      type,
      refundReason,
      financialAdjustmentDescription,
    } = data;
    const sharedData: BaseTransactionTableRow = {
      cardBrand: cardBrand || emptyPlaceholder,
      cardType: !!cardType ? capitalize(cardType) : emptyPlaceholder,
      date: timestamp ? format(new Date(timestamp), DATE_FORMAT) : emptyPlaceholder,
      disbursed: arrivalDate ? format(new Date(arrivalDate?.replace(/Z$/, '')), 'MM/dd/yy') : emptyPlaceholder,
      last4: last4 || emptyPlaceholder,
      terminal: terminalName || emptyPlaceholder,
    };

    const amount = data.amount || 0;
    const transactionType = getTransactionTypeDescription({
      type,
      amount: data.stripeRefundPaymentIntentAmount || 0,
      refundedAmount: amount,
      adjustmentDescription: financialAdjustmentDescription,
    });

    const interactiveData: InteractiveTransactionTableRow = {
      ...sharedData,
      actions: emptyPlaceholder,
      amount: formatMoney(amount),
      clientTransactionFee: clientFee ? formatMoney(clientFee) : emptyPlaceholder,
      clinicPetParentName:
        clientName && clientPetParentId ? (
          <Tooltip label={clientName}>
            <Link
              size="sm"
              variant="success"
              onClick={(): void => {
                if (callbacks?.onPetParentClick) {
                  callbacks.onPetParentClick(clientPetParentId);
                }
              }}
            >
              {clientName}
            </Link>
          </Tooltip>
        ) : (
          emptyPlaceholder
        ),
      clinicPetParentNameFiltering: clientName || '',
      conversation: channelId ? (
        <Link
          size="sm"
          variant="success"
          onClick={(): void => {
            if (channelId) {
              callbacks?.onConversationClick(channelId);
            }
          }}
        >
          View
        </Link>
      ) : (
        emptyPlaceholder
      ),
      developerMode: (
        <Link
          size="sm"
          variant="success"
          onClick={(): void => {
            callbacks?.onDeveloperLinkClick(data);
          }}
        >
          {transactionId}
        </Link>
      ),
      netProceeds: formatMoney(netProceeds || 0),
      paymentMethod: paymentMedium ? (
        <TooltipText tooltipText={terminalName || undefined} text={paymentMedium} />
      ) : (
        emptyPlaceholder
      ),
      paymentStatus: paymentStatus
        ? getStatusComponent({ status: paymentStatus, tooltipText: refundReason })
        : emptyPlaceholder,
      sentBy: sentBy ? <TooltipText tooltipText={sentBy} text={sentBy} /> : emptyPlaceholder,
      totalInvoiced: totalInvoiced ? formatMoney(totalInvoiced) : emptyPlaceholder,
      transactionFees: transactionFees ? formatMoney(transactionFees) : emptyPlaceholder,
      transactionType: <TooltipText text={transactionType} />,
    };

    const downloadData: DownloadTransactionTableRow = {
      ...sharedData,
      amount: centsToDollars(amount),
      clientTransactionFee: clientFee ? centsToDollars(clientFee) : emptyPlaceholder,
      clinicPetParentName: clientName || emptyPlaceholder,
      netProceeds: centsToDollars(netProceeds || 0),
      paymentMethod: paymentMedium || emptyPlaceholder,
      paymentStatus: paymentStatus ? StatusDescription[paymentStatus] : emptyPlaceholder,
      sentBy: sentBy || emptyPlaceholder,
      totalInvoiced: totalInvoiced ? centsToDollars(totalInvoiced) : emptyPlaceholder,
      transactionFees: transactionFees ? centsToDollars(transactionFees) : emptyPlaceholder,
      transactionType,
      invoices: emptyPlaceholder,
      writebackStatus: emptyPlaceholder,
    };

    // Payment Intent
    if (data.stripePaymentIntentId) {
      if (isInteractiveDisplay) {
        const intent: PaymentReportStripePaymentIntent = {
          id: data.stripePaymentIntentId,
          amount: data.amount || 0,
          amountCaptured: data.stripePaymentIntentAmountCaptured || 0,
          refundedAmount: data.stripePaymentIntentTotalRefunded || 0,
          clientServiceFee: data.clientFee || 0,
          paidAt: data.stripePaymentIntentPaidAt,
          paymentIdentifier: data.paymentIdentifier,
          status: data.paymentStatus,
          stripePaymentMethod: {
            cardBrand: data.cardBrand,
            last4: data.last4,
            funding: data.cardType,
          },
          stripeReceiptId: data.stripeReceiptId,
        };
        interactiveData.actions =
          callbacks?.setRefundPaymentIntent && paymentStatus !== StripePaymentIntentStatus.Canceled ? (
            <PaymentMoreButton
              intent={intent}
              invoiceId={data.invoiceId || ''}
              clinicPetParentId={clientPetParentId || ''}
              petParentEmail={data.clientPetParentEmail}
              setRefundPaymentIntent={callbacks?.setRefundPaymentIntent}
            />
          ) : (
            emptyPlaceholder
          );
      }

      if (hasInvoiceDiscovery) {
        const invoices = data.invoiceIdentifiers ? data.invoiceIdentifiers.split(',') : [];
        let invoiceNumbers = '';
        if (data.paymentRelationship) {
          invoiceNumbers = getBalanceTerminology(primaryIntegrationName, true);
        }

        invoices.forEach((invoice) => {
          if (isInteractiveDisplay) {
            const identifier = getInvoiceIdentifierCopy(invoice);
            invoiceNumbers = invoiceNumbers ? `${invoiceNumbers}, ${identifier}` : identifier;
          } else {
            invoiceNumbers += `${invoiceNumbers.length ? '; ' : ''}${invoice}`;
          }
        });

        interactiveData.invoices = invoiceNumbers;
        downloadData.invoices = invoiceNumbers;
      } else {
        delete downloadData.invoices;
      }

      if (hasBitwerxPaymentWritebacks) {
        const isIntegratedPayment = !!data.paymentRelationship || !!data.invoiceIdentifiers;
        const isPaymentSuccessful = paymentStatus === StripePaymentIntentStatus.Succeeded;
        const writebackStatus = getWritebackStatus({
          writebackStatus: data.writebackStatus,
          writebackError: data.writebackError,
          isPaymentConfigInvalid,
          isIntegratedPayment,
        });

        if (isInteractiveDisplay) {
          interactiveData.writebackStatus =
            isPaymentSuccessful && callbacks ? (
              <WritebackStatus
                writebackStatus={writebackStatus}
                paymentIntentId={data.stripePaymentIntentId}
                popoverDescription={WritebackStatusDescription[writebackStatus]({
                  hasAdminRoleAtCurrentClinic,
                  pimsName: primaryIntegrationName || 'your PIMS',
                })}
                isPaymentConfigInvalid={isPaymentConfigInvalid}
                isIntegratedPayment={isIntegratedPayment}
              />
            ) : (
              emptyPlaceholder
            );
        } else {
          downloadData.writebackStatus =
            isPaymentSuccessful && writebackStatus ? writebackStatusDisplayValue[writebackStatus] : emptyPlaceholder;
        }
      } else {
        delete downloadData.writebackStatus;
      }
    }

    if (isClientIdSupported) {
      interactiveData.clinicPetParentPimsId = data.clientPimsId || emptyPlaceholder;
      downloadData.clinicPetParentPimsId = data.clientPimsId || emptyPlaceholder;
    }

    const finalRow: TransactionTableRow = {
      ...(isInteractiveDisplay ? interactiveData : downloadData),
    };

    tableData.push(finalRow);
  }

  if (!isInteractiveDisplay && !!columns?.length) {
    const transactionColumnsToRemove = columns
      .filter((col) => col.omit)
      .map((col) => col.selector) as (keyof TransactionTableRow)[];

    for (const row of tableData) {
      transactionColumnsToRemove.forEach((columnName) => {
        delete row[columnName];
      });
    }
  }

  return tableData;
};
